/**
 * One request (design "Traces: one request"): its entry, correlationId, duration, spans and errors on top; the timing
 * chart, every step as a bar on one clock (indented: called by the row above; the faded bar is the time a message
 * waited in a topic; the failing step red with "✕"); beside it (below, on a phone) the picked step: its error in words,
 * its details, its service's log lines for this request and the links: the code at the line, the store in Data, the
 * endpoint in API (when this build has the API tab) and "Ask the AI about it". A failed request leads with what it
 * came to in one sentence (vfs-service's `outcome`), its service in bold: for one that never reached its service (the
 * preview proxy answered by itself) also Open logs (that service's, at the request's time), Start or Restart when it
 * is stopped or crashed (`targetActions`, from the panel) and that service's lines around the request.
 */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ChatCircleText, Terminal } from '@phosphor-icons/react';
import { axisTicks, barLayout, clockLength, formatMs } from './waterfallLayout';
import {
  ERROR_COLOUR, clockTime, codeLabel, colourOf, dataFocusOf, defaultRow, fullTime, headerCounts, isoTime, logLine, logsAroundOf, logsServiceOf, offsetLabel,
  outcomeOf, plural, rowLogs, rowTitle, sentenceParts, shortId, storeName,
} from './tracesModel';

function Swatch({ colour }) {
  return <span aria-hidden className="traces-swatch" style={{ background: colour }} />;
}

Swatch.propTypes = { colour: PropTypes.string };

/** The plain sentence of a failed request, its service in bold, and what to do about it. */
function Outcome({ trace, onOpenLogs = null, onAsk = null, targetActions = null }) {
  const outcome = outcomeOf(trace);
  if (!outcome?.text) { return null; }
  const unreached = outcome.kind === 'unreached';
  const target = outcome.target || null;
  const stateNow = target?.stateNow && target.stateNow !== target.state ? target.stateNow : null;
  return (
    <section aria-label="What happened" className={`traces-outcome${unreached ? ' is-unreached' : ''}`} data-testid="traces-outcome">
      <p className="traces-outcome-text">
        {sentenceParts(outcome.text, outcome.service).map((part, i) => (part.strong ? <strong key={i}>{part.text}</strong> : <React.Fragment key={i}>{part.text}</React.Fragment>))}
      </p>
      {unreached && target && (
        <p className="traces-outcome-facts">
          <span className="traces-mono">services/{target.service}</span>
          {target.state && <span>state when it failed: {target.state}</span>}
          {stateNow && <span>now: {stateNow}</span>}
          {target.port ? <span>port {target.port}</span> : null}
          {target.path && <span className="traces-mono">{target.path}</span>}
        </p>
      )}
      <div className="traces-links">
        {onOpenLogs && outcome.service && (
          <button className="traces-pill is-link" onClick={() => onOpenLogs(outcome.service, { at: trace.start })} type="button"><Terminal aria-hidden size={14} />Open logs</button>
        )}
        {unreached && targetActions}
        {onAsk && (
          <button className="traces-pill is-link is-ai" onClick={() => onAsk(null)} type="button"><ChatCircleText aria-hidden size={14} />Ask the AI about it</button>
        )}
      </div>
    </section>
  );
}

Outcome.propTypes = { onAsk: PropTypes.func, onOpenLogs: PropTypes.func, targetActions: PropTypes.node, trace: PropTypes.object.isRequired };

function StepPanel({ trace, row, links }) {
  const logs = rowLogs(trace, row);
  const around = row.service === 'Browser' ? logsAroundOf(trace) : [];
  const service = logsServiceOf(row);
  const error = row.error || null;
  const focus = dataFocusOf(row);
  return (
    <aside aria-labelledby="traces-step-title" className="traces-card traces-step" data-testid="traces-step">
      <h2 id="traces-step-title"><Swatch colour={row.status === 'error' ? ERROR_COLOUR : colourOf(row)} />{rowTitle(row)}</h2>
      {error && (error.words || error.message) && (
        <div className="traces-error" data-testid="traces-step-error">
          <p><span className="traces-sr">Error: </span>{error.words || error.message}</p>
          {error.words && error.message && error.message !== error.words && <p className="traces-mono">{error.message}</p>}
        </div>
      )}
      {(row.details || []).length > 0 && (
        <dl className="traces-kv">
          {row.details.map((d) => (
            <React.Fragment key={d.key}>
              <dt>{d.key}</dt>
              <dd className={d.mono ? 'traces-mono' : undefined}>{d.value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
      {trace.logsAround && row.service === 'Browser' && (
        <div>
          <h3>{trace.logsAround.service} logs around this request</h3>
          {around.length
            ? <pre className="traces-logs" data-testid="traces-logs-around">{around.map(logLine).join('\n')}</pre>
            : <p className="traces-muted">{trace.logsAround.service} printed nothing in the 5 seconds around this request</p>}
          {trace.logsAround.approximate && around.length > 0 && <p className="traces-muted">Its latest lines: this sandbox does not keep their time.</p>}
        </div>
      )}
      {service && !(trace.logsAround && row.service === 'Browser') && (
        <div>
          <h3>{service} logs for this request</h3>
          {logs.length
            ? <pre className="traces-logs" data-testid="traces-logs">{logs.map(logLine).join('\n')}</pre>
            : <p className="traces-muted">No log lines for this request</p>}
        </div>
      )}
      <div className="traces-links">
        {row.code?.path && links.onOpenCode && (
          <button className="traces-pill is-link" onClick={() => links.onOpenCode(row.code)} type="button">Open the code ({codeLabel(row.code)})</button>
        )}
        {focus && links.onOpenData && (
          <button className="traces-pill is-link" onClick={() => links.onOpenData(focus)} type="button">See in Data › {storeName(row.store)}</button>
        )}
        {row.endpoint && links.onOpenApi && (
          <button className="traces-pill is-link" onClick={() => links.onOpenApi(row.endpoint)} type="button">Open in API</button>
        )}
        {links.onAsk && (
          <button className="traces-pill is-link is-ai" onClick={() => links.onAsk(row)} type="button"><ChatCircleText aria-hidden size={14} />Ask the AI about it</button>
        )}
      </div>
    </aside>
  );
}

StepPanel.propTypes = { links: PropTypes.object.isRequired, row: PropTypes.object.isRequired, trace: PropTypes.object.isRequired };

export default function TraceView({
  trace, onBack, onOpenCode = null, onOpenData = null, onOpenApi = null, onAsk = null, onOpenLogs = null, targetActions = null,
}) {
  const rows = useMemo(() => trace?.rows || [], [trace]);
  const [pickedId, setPickedId] = useState(() => defaultRow(trace)?.id || null);
  const picked = rows.find((r) => r.id === pickedId) || defaultRow(trace);
  const clock = clockLength(rows, trace.durationMs);
  const ticks = useMemo(() => axisTicks(clock), [clock]);
  const rootEndpoint = rows.find((r) => r.endpoint)?.endpoint || null;
  const links = { onOpenCode, onOpenData, onOpenApi, onAsk };
  const errors = trace.errorCount || rows.filter((r) => r.status === 'error').length;

  return (
    <div className="traces-trace" data-testid="traces-trace">
      <button className="traces-back" onClick={onBack} type="button"><span aria-hidden>← </span><span className="traces-sr">Back to </span>Traces</button>
      <div className="traces-trace-head">
        <h1>{trace.entry}{outcomeOf(trace)?.service && <span className="traces-h1-target"> → {outcomeOf(trace).service}</span>}</h1>
        <span className="traces-pill traces-mono" title={trace.correlationId}>correlationId {shortId(trace.correlationId || trace.traceId, 8, 3)}</span>
        <span className="traces-pill">{formatMs(trace.durationMs)}</span>
        <span className="traces-pill">{headerCounts(trace)}</span>
        {errors > 0 && <span className="traces-pill is-bad">{plural(errors, 'error')}</span>}
        <span className="traces-grow" />
        {trace.start ? <time className="traces-pill" dateTime={isoTime(trace.start)} title={fullTime(trace.start)}>{clockTime(trace.start, Date.now(), { millis: true })}</time> : null}
        {rootEndpoint && onOpenApi && <button className="traces-pill is-link" onClick={() => onOpenApi(rootEndpoint)} type="button">Open in API</button>}
        {onAsk && !outcomeOf(trace) && <button className="traces-pill is-link is-ai" onClick={() => onAsk(null)} type="button"><ChatCircleText aria-hidden size={14} />Ask the AI about it</button>}
      </div>
      <Outcome onAsk={onAsk} onOpenLogs={onOpenLogs} targetActions={targetActions} trace={trace} />

      <div className="traces-trace-body">
        <section aria-label="Timing chart" className="traces-card traces-waterfall">
          <div aria-hidden className="traces-span is-head">
            <span>Service · operation</span>
            <span className="traces-axis">
              {ticks.map((t, i) => (
                <span className={i === ticks.length - 1 ? 'is-end' : i === 0 ? 'is-start' : undefined} key={t.ms} style={{ left: `${t.pct}%` }}>{t.label}</span>
              ))}
            </span>
            <span>time</span>
          </div>
          <ul aria-label="Steps" className="traces-span-list">
            {rows.map((row) => {
              const on = picked?.id === row.id;
              const bad = row.status === 'error';
              const bar = barLayout(row, clock);
              const colour = bad ? ERROR_COLOUR : colourOf(row);
              return (
                <li key={row.id}>
                  <button
                    aria-current={on ? 'true' : undefined}
                    className={`traces-span${on ? ' is-on' : ''}${bad ? ' is-bad' : ''}`}
                    data-testid={`traces-span-${row.id}`}
                    onClick={() => setPickedId(row.id)}
                    type="button"
                  >
                    <span className="traces-span-name" style={{ paddingLeft: `${Math.min(row.depth || 0, 8) * 14}px` }}>
                      <Swatch colour={colour} />
                      <span className="traces-span-label" title={rowTitle(row)}>{row.label} · <span className="traces-mono">{row.operation}</span></span>
                    </span>
                    <span className="traces-track">
                      <span className={`traces-bar${row.kind === 'wait' ? ' is-wait' : ''}`} style={{ left: `${bar.left}%`, width: `${bar.width}%`, background: colour }} />
                    </span>
                    <span className="traces-span-time">
                      <span>
                        {formatMs(row.durationMs)}
                        {bad && <><span aria-hidden> ✕</span><span className="traces-sr">, failed</span></>}
                        {row.kind === 'wait' && <span className="traces-sr">, waiting in the topic</span>}
                      </span>
                      <span className="traces-span-start"><span className="traces-sr">, started at </span>{offsetLabel(row)}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="traces-footnote is-bordered">Bars: when each step started and how long it took, on one clock. Indented: called by the row above. The faded bar is the time a message waited in the topic before the consumer took it.</p>
        </section>
        {picked && <StepPanel links={links} row={picked} trace={trace} />}
      </div>
    </div>
  );
}

TraceView.propTypes = {
  onAsk: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onOpenApi: PropTypes.func,
  onOpenCode: PropTypes.func,
  onOpenData: PropTypes.func,
  // (service, { at }) => void: that service's logs, at the request's time.
  onOpenLogs: PropTypes.func,
  // Start / Restart for the service a request never reached (TracesPanel's TargetActions).
  targetActions: PropTypes.node,
  trace: PropTypes.object.isRequired,
};
